import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';

import { MAX_TAG_NAME_LENGTH, normalizeTagName } from '#common/tags';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { useGetTagProductsQuery, useGetTagsQuery, useSaveTagMutation } from '#services/tag';
import { useInitialLoadOverlay, useQueryErrorSnackbar, type TagRow } from './utils';

interface TagProductRow {
  id: number;
  name?: string;
  inactivated?: boolean;
}

interface SaveTagError {
  status?: number;
  data?: {
    mergeInto?: { id: number; name: string };
    affectedCount?: number;
  };
}

export default function TagEdit() {
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;
  const navigate = useNavigate();
  const location = useLocation();
  const tagId = Number(new URLSearchParams(location.search).get('tagId')) || undefined;
  const isEdit = Boolean(tagId);

  // No single-tag endpoint; the list query is cached from the List page.
  const query = useGetTagsQuery(undefined, { skip: !isEdit });
  const tag = ((query.data || []) as TagRow[]).find((t) => t.id === tagId);
  const [saveTag] = useSaveTagMutation();
  const productsQuery = useGetTagProductsQuery({ id: tagId }, { skip: !isEdit, refetchOnMountOrArgChange: true });
  const [name, setName] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(30);

  useInitialLoadOverlay(query.isLoading || productsQuery.isLoading);
  useQueryErrorSnackbar(query);
  // A merge deletes this tag and the Tags refetch then 404s; the page already
  // handles a missing tag, so don't let that snackbar replace the merge notice.
  const productsGone = (productsQuery.error as { status?: number } | undefined)?.status === 404;
  useQueryErrorSnackbar(productsGone ? {} : productsQuery);

  useEffect(() => {
    setName(tag?.name ?? '');
  }, [tag?.id, tag?.name]);

  const normalized = normalizeTagName(name);
  const tooLong = normalized.length > MAX_TAG_NAME_LENGTH;
  const canSave = Boolean(normalized) && !tooLong && (!isEdit || (tag && normalized !== tag.name));

  const submit = (confirmMerge?: boolean) => {
    showBackdrop();
    void saveTag({ id: tagId, name: normalized, confirmMerge }).then((res) => {
      const error = (res as { error?: SaveTagError }).error;
      const mergeInto = error?.status === 409 ? error.data?.mergeInto : undefined;
      if (mergeInto && tag) {
        const count = error?.data?.affectedCount ?? 0;
        setDialogData({
          title: 'Merge tags',
          text: `"${normalized}" already exists as "${mergeInto.name}". Move ${count} product${count === 1 ? '' : 's'} to "${mergeInto.name}" and delete "${tag.name}"?`,
          disagree: 'Cancel',
          agree: 'Merge',
          onDisagree: () => hideDialog(),
          onAgree: () => {
            hideDialog();
            submit(true);
          },
        });
        showDialog();
        return;
      }

      const hasError = simpleSnackbar(
        res as unknown as Parameters<typeof simpleSnackbar>[0],
        confirmMerge ? `Tag "${tag?.name}" was merged into "${(res.data as TagRow | undefined)?.name}".` : `Tag "${normalized}" was successfully saved.`,
      );
      if (!hasError) void navigate('/management/tag/list');
    }).finally(() => {
      hideBackdrop();
    });
  };

  // ponytail: client-side sort/paging; the endpoint returns every product.
  const products = (productsQuery.data || []) as TagProductRow[];
  const sortedProducts = [...products].sort((a, b) => {
    const diff = orderBy === 'status'
      ? Number(Boolean(a.inactivated)) - Number(Boolean(b.inactivated))
      : String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
    return order === 'asc' ? diff : -diff;
  });

  const productTableProps: EnhancedTableProps = {
    name: 'tag-products',
    headers: [{
      id: 'name',
      label: 'Product',
      renderer: (row) => {
        const product = row as unknown as TagProductRow;
        const url = `/management/product/edit?productId=${product.id}`;
        return (
          <Link
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
          >
            {product.name || `#${product.id}`}
          </Link>
        );
      },
    }, {
      id: 'status',
      label: 'Status',
      renderer: (row) => {
        const product = row as unknown as TagProductRow;
        return product.inactivated
          ? <Chip size="small" label="Inactive" />
          : <Chip size="small" color="success" variant="outlined" label="Active" />;
      },
    }],
    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize: (size) => {
      setPageSize(size);
      setPageIndex(0);
    },
    rowCount: sortedProducts.length,
    rows: sortedProducts.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize) as unknown as EnhancedTableRow[],
  };

  if (isEdit && query.isSuccess && !tag) {
    return <Box>Tag not found.</Box>;
  }

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        {isEdit ? 'Edit Tag' : 'Add Tag'}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, pb: 1, mb: 1 }}>
        <TextField
          required
          fullWidth
          size="small"
          name="name"
          label="Name"
          value={name}
          error={tooLong}
          helperText={tooLong ? `Tag name must be at most ${MAX_TAG_NAME_LENGTH} characters.` : undefined}
          onChange={(e) => setName(e.target.value)}
        />
      </Paper>

      <Button
        variant="outlined"
        disabled={!canSave}
        onClick={() => submit()}
      >
        Save
      </Button>

      {isEdit && productsQuery.isSuccess && (
        <>
          <Box data-testid="tag-products-title" sx={{ typography: 'h7', mt: 3 }}>
            {`Products (${products.length})`}
          </Box>
          {products.length ? (
            <EnhancedTable {...productTableProps} />
          ) : (
            <Box data-testid="tag-products-empty" sx={{ p: 2, color: 'text.secondary' }}>
              No products use this tag.
            </Box>
          )}
        </>
      )}
    </>
  );
}
